import { EndPoints, httpService, Operation } from "@/Core/Client";
import { useState } from "react";

import { Config } from "@/components/crud/crud-config";
import CrudPage from "@/components/crud/page";
import { DynamicBadge } from "@/components/dynamic-badge";
import TableActions from "@/components/table-actions";
import { getPaymentStatusColors } from "@/components/table-utils/get-status-color";
import useQueryKeys from "@/config/query-keys";
import { DATE_FORMAT_OPTIONS } from "@/Core/Helpers/DayjsHelpers";
import {
    PaymentFormDto,
    PaymentResponseDto,
} from "@/Dtos/payment-dto";
import DateCell from "@/ui/date-cell";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import { Checkbox } from "rizzui";
import { useModal } from "../shared/modal-views/use-modal";

const config: Config<PaymentResponseDto, PaymentFormDto> = {
    endPoint: EndPoints.payments.crud,
    queryKey: useQueryKeys.payments,
    columns: [],
    formOptions: {
        customSize: "1200px",
        sendAsFormData: true,
        formClassName:
            "grid grid-cols-1 gap-5 @container md:grid-cols-2 lg:grid-cols-3 [&_label]:font-medium",
    },

    fields: [
        {
            name: "name",
            label: "Title",
            type: "text",
            hasFilter: true,
            filter: {
                operation: Operation.like,
            },
        },
        {
            name: "host",
            label: "Host",
            type: "text",
            hasFilter: true,
            // className: "w-auto",
            referEndPoint: EndPoints.entity.referTo,
            filter: {
                operation: Operation.eq,
                // showOperations: true,
            },
        },
        {
            name: "venue",
            label: "Venue",
            type: "text",
            hasFilter: true,
            referEndPoint: EndPoints.role.referTo,
            filter: {
                operation: Operation.eq,
            },
        },
        {
            name: "capacity",
            label: "Participants",
            type: "number",
            hasFilter: false,
        },
        {
            name: "seats",
            label: "Booked Seats",
            type: "number",
            hasFilter: false,
        },
        {
            name: "status",
            label: "Status",
            type: "select",
            options: [
                {
                    label: "Male",
                    value: "male" as string,
                },
                {
                    label: "Female",
                    value: "female" as string,
                },
            ],
            hasFilter: true,
            filter: {
                operation: Operation.eq,
            },
            props: {
                // getOptionLabel: (option) => option.label,
                // getOptionValue: (option) => option.value,
                optionClassName: "capitalize",
            },
        },
        {
            name: "picture",
            label: "Picture",
            type: "file",
        },
        {
            name: "password",
            label: "Password",
            type: "password",
            createFormOnly: true,
        },
        {
            name: "confirmPassword",
            label: "Confirm Password",
            type: "password",
            createFormOnly: true,
        },

        {
            name: "createdAt",
            label: "Created At",
            type: "date",
            hasFilter: true,
            filterOnly: true,
            dateFormat: DATE_FORMAT_OPTIONS.L,
            filter: {
                operation: Operation.gte,
            },
        },
    ],
    titles: {
        pageTitle: "Payments",
    },
    filterProps: {
        useDrawer: true,
        sortingClassName: "w-auto",
        directionClassName: "w-auto",
    },
};

const columnHelper = createColumnHelper<PaymentResponseDto>();

const Columns = [
    columnHelper.display({
        id: "select",
        size: 50,
        header: ({ table }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select all Rows"
                checked={table.getIsAllPageRowsSelected()}
                onChange={() => table.toggleAllPageRowsSelected()}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                className="ps-3.5"
                aria-label="Select Row"
                checked={row.getIsSelected()}
                onChange={row.getToggleSelectedHandler()}
            />
        ),
    }),

    columnHelper.accessor("reference", {
        id: "reference",
        size: 200,
        header: "Reference",
        enableSorting: false,
        cell: ({ row }) => row.original.reference,
    }),
    columnHelper.accessor("amount", {
        id: "amount",
        size: 150,
        header: "Amount",
        cell: ({ row }) => (row.original.currency === "USD" ? "$ " : "SLSH ") + row.original.amount,
    }),
    columnHelper.accessor("source", {
        id: "source",
        size: 140,
        header: "Source",
        cell: ({ row }) => row.original.source,
    }),
    columnHelper.accessor("paymentMethod", {
        id: "paymentMethod",
        size: 150,
        header: "Account",
        cell: ({ row }) => row.original.paymentMethod.account,
    }),
    columnHelper.accessor("status", {
        id: "status",
        size: 140,
        header: "Status",
        cell: ({ row: { original } }) => (
            <DynamicBadge
                status={original.status}
                classNameBadge={getPaymentStatusColors(original.status)[1]}
                classNameText={getPaymentStatusColors(original.status)[0]}
            />
        ),
    }),
    columnHelper.accessor("paidAt", {
        id: "paidAt",
        size: 160,
        header: "Date",
        cell: ({ row }) => (
            <DateCell
                date={new Date(row.original.paidAt ?? "")}
            />
        ),
    }),
    columnHelper.display({
        id: "actions",
        size: 80,
        cell: ({ row }) => {
            const { openModal } = useModal();
            const [isLoading, setLoading] = useState(false);
            const queryClient = useQueryClient();

            return (
                <TableActions
                    row={row.original}
                    onDelete={() => {
                        setLoading(true);
                        httpService(config.endPoint).delete(row.original.id!);
                        queryClient.refetchQueries({
                            queryKey: config.queryKey,
                        });
                        setLoading(false);
                    }}
                    actions={[]}
                />
            );
        },
    }),
];

config.columns = Columns;

export default function MainPage() {
    return CrudPage<PaymentResponseDto, PaymentFormDto>({ config: config });
}

